import { Injectable } from '@angular/core';
import { WebApiClient } from '../web-api-client.service';
import { WebApiService } from '../web-api.service';

/** 母子商品換算快取資料 */
export interface ParentConversionCacheDto {
  /** 母商品識別碼 */
  ParentProductId: number;
  /** 母商品單位 */
  ParentUnit: string;
  /** 子商品識別碼 */
  ChildProductId: number;
  /** 子商品貨號 */
  ChildProductCode: string;
  /** 子商品名稱 */
  ChildProductName: string;
  /** 子商品單位 */
  ChildUnit: string;
  /** 換算比例 */
  ConversionQty: number;
}

/** 母子商品換算快取服務 */
@Injectable({ providedIn: 'root' })
export class ProductParentCacheService {
  /** 快取：ParentProductId → 換算資訊 */
  private cache: Map<number, ParentConversionCacheDto> = new Map();
  /** 反向快取：ChildProductId → 換算資訊 */
  private reverseCache: Map<number, ParentConversionCacheDto> = new Map();
  /** 載入中的 Promise (批次載入用) */
  private loadingMap: Map<number, Promise<ParentConversionCacheDto>> = new Map();

  /** API Service */
  private api: WebApiService;

  constructor(private webApiClient: WebApiClient) {
    this.api = this.webApiClient.createHisService('stocks/ProductParent');
  }

  /**
   * 取得母商品換算資訊
   * @param productId 母商品識別碼
   * @returns 換算資訊 (若無換算設定則回傳 null)
   */
  async getConversionInfo(productId: number): Promise<ParentConversionCacheDto> {
    // 已快取
    if (this.cache.has(productId)) {
      return this.cache.get(productId);
    }

    // 正在載入中，等待完成
    if (this.loadingMap.has(productId)) {
      return this.loadingMap.get(productId);
    }

    // 快取未命中，呼叫 API 載入單筆
    const loadingPromise = this.api.post('GetConversionInfo', [productId])
      .toPromise()
      .then((data: ParentConversionCacheDto[]) => {
        const item = (data || []).find(d => d.ParentProductId === productId);
        if (item) {
          this.cache.set(productId, item);
          this.reverseCache.set(item.ChildProductId, item);
        }
        this.loadingMap.delete(productId);
        return item || null;
      })
      .catch(error => {
        console.error(`載入母商品 ${productId} 換算資訊失敗`, error);
        this.loadingMap.delete(productId);
        return null;
      });

    this.loadingMap.set(productId, loadingPromise);
    return loadingPromise;
  }

  /**
   * 預載多個母商品的換算資訊
   * @param productIds 母商品識別碼陣列
   */
  async preloadConversionInfo(productIds: number[]): Promise<void> {
    const notCachedIds = productIds.filter(id => !this.cache.has(id));
    if (notCachedIds.length === 0) {
      return;
    }

    // 批次載入
    try {
      const data = await this.api.post('GetConversionInfo', notCachedIds)
        .toPromise() as ParentConversionCacheDto[];

      (data || []).forEach(item => {
        this.cache.set(item.ParentProductId, item);
        this.reverseCache.set(item.ChildProductId, item);
      });
    } catch (error) {
      console.error('批次載入母子商品換算資訊失敗', error);
    }
  }

  /**
   * 取得換算提示文字（同步方法，僅從快取讀取）
   * @param productId 母商品識別碼
   * @param qty 母商品數量
   * @returns 提示文字，例如 "(= 50 支)"，若無快取資料則回傳空字串
   */
  getConversionHint(productId: number, qty: number): string {
    if (!productId || !qty) {
      return '';
    }

    const conversion = this.cache.get(productId);
    if (!conversion) {
      return '';
    }

    const convertedQty = qty * conversion.ConversionQty;
    const unit = conversion.ChildUnit || '';
    return `(= ${convertedQty} ${unit})`;
  }

  /**
   * 清除快取
   * @param productId 若指定則只清除該商品，否則清除全部
   */
  clearCache(productId?: number): void {
    if (productId !== undefined) {
      // 同時清除反向快取
      const item = this.cache.get(productId);
      if (item) {
        this.reverseCache.delete(item.ChildProductId);
      }
      this.cache.delete(productId);
      this.loadingMap.delete(productId);
    } else {
      this.cache.clear();
      this.reverseCache.clear();
      this.loadingMap.clear();
    }
  }

  /**
   * 檢查特定商品是否已快取
   * @param productId 母商品識別碼
   */
  isCached(productId: number): boolean {
    return this.cache.has(productId);
  }

  /**
   * 取得快取商品數量
   */
  getCacheCount(): number {
    return this.cache.size;
  }

  /**
   * 反向查詢：以子商品 ID 取得母商品換算資訊（同步，僅從快取讀取）
   * @param childProductId 子商品識別碼
   * @returns 換算資訊，若無則回傳 null
   */
  getParentInfoByChildId(childProductId: number): ParentConversionCacheDto | null {
    return this.reverseCache.get(childProductId) || null;
  }

  /**
   * 同步取得快取中的換算資訊（不觸發 API）
   * @param parentProductId 母商品識別碼
   * @returns 換算資訊，若無則回傳 null
   */
  getCachedConversionInfo(parentProductId: number): ParentConversionCacheDto | null {
    return this.cache.get(parentProductId) || null;
  }
}
